//#region ../src/storage/range-probe.ts
/**
* The range-vs-fallback policy for opening a hosted archive over HTTP, as one
* pure function.
*
* A transport probes a hosted URL with a HEAD (total size from the
* CORS-safelisted `Content-Length`) plus a `Range: bytes=0-0` GET (support
* detection), then asks `decideFallback` what to do. Keeping it pure means
* every branch — including the ones that are a pain to trigger against a real
* cloud provider — is unit-testable with no network.
*/
/** Total from a `Content-Range: bytes <range>/<total>` header, or null if
*  unknown/absent/malformed. A `206` always carries this header (or the
*  unsatisfied-range form with an asterisk in place of the range), so when a
*  host — or a proxy under the caller's control — exposes it, the archive can
*  be sized even when HEAD gives no `Content-Length`. `TOTAL` may itself be an
*  asterisk (unknown), in which case there is nothing usable. */
function parseContentRangeTotal(header) {
	if (!header) return null;
	const m = /^bytes\s+(?:\d+-\d+|\*)\/(\d+)$/.exec(header.trim());
	if (!m) return null;
	const total = Number(m[1]);
	return Number.isSafeInteger(total) ? total : null;
}
/**
* Is this status the host saying the resource is DELETED, as opposed to
* temporarily unreachable?
*
* Exists as one named predicate because the answer was given three different
* ways in one transport (PR #376 review): `remote-range-byte-source` tested
* `404 || 410`, this module and `open-remote-archive` tested only `404`, and
* the union above documented only `404`. The visible consequence: an archive
* deleted from a host that answers 410 reached the user as "That link cannot
* be opened as an archive" instead of "That file does not exist" — a wrong
* diagnosis of a correct server response.
*
* 410 Gone is a STRONGER statement than 404: the host asserts the resource
* existed and was removed. Anything that treats 404 as definitive must treat
* 410 as at least as definitive.
*/
function isDefinitivelyGone(status) {
	return status === 404 || status === 410;
}
/** Boundary defense: even if a caller lets an unvalidated size (NaN, a
*  negative, a float) through, it must never anchor a range-reading parser —
*  a bogus size corrupts every read. */
function isUsableArchiveSize(size) {
	return size !== null && Number.isSafeInteger(size) && size >= 0;
}
function decideFallback(probe) {
	if (probe.status === 206) {
		if (isUsableArchiveSize(probe.size)) return {
			mode: "ranges",
			size: probe.size
		};
		return { mode: "full-download" };
	}
	if (probe.status === 200 && probe.body !== void 0) {
		if (isUsableArchiveSize(probe.size) && probe.body.length !== probe.size) return {
			mode: "reject",
			cause: "corrupt"
		};
		return {
			mode: "eager-local",
			body: probe.body
		};
	}
	if (isDefinitivelyGone(probe.status)) return {
		mode: "reject",
		cause: "missing"
	};
	if (probe.status === 416) return {
		mode: "reject",
		cause: "corrupt"
	};
	return {
		mode: "reject",
		cause: "unusable-link"
	};
}
//#endregion
export { decideFallback, isDefinitivelyGone, parseContentRangeTotal };
